import {
	Accordion,
	Button,
	Checkbox,
	Form,
	Grid,
	Header,
	Input,
	Label,
	Message,
	Segment,
	type SemanticWIDTHS,
	Statistic,
} from '@mairistem/composants';
import { Icon } from '@mairistem/finances-utils';
import React from 'react';
import {
	LIBELLES_RESUME,
	NIVEAUX,
	ORDRE_NIVEAUX,
} from '../../constants/pes/importPes';
import type {
	CompteRenduImportPes,
	NiveauAnomalieImportPes,
} from '../../services/pes/importPes';
import {
	cleAnomalie,
	compterParNiveau,
	telechargerCsv,
	texteAnomalie,
} from '../../utils/pes/compteRendu';
import { normaliser, pluriel } from '../../utils/pes/format';
import GroupeAnomalie from './GroupeAnomalie';

/** Au-delà, les bloquants ne sont plus dépliés d'office : la page resterait lisible. */
const BLOQUANTS_DEPLIES = 5;

interface CompteRenduImportProps {
	/** analyse (rien n'est écrit) ou import (écrit si le fichier est importable) */
	etape: 'analyse' | 'import';
	compteRendu: CompteRenduImportPes;
	nomFichier: string;
}

/** Verdict en tête du compte rendu : ce que l'utilisateur doit retenir sans rien déplier. */
const Verdict = ({
	etape,
	compteRendu,
	nombres,
}: Omit<CompteRenduImportProps, 'nomFichier'> & {
	nombres: Record<NiveauAnomalieImportPes, number>;
}) => {
	const bordereaux = compteRendu.resume.bordereauxCrees;
	const avertissements =
		nombres.avertissement > 0
			? ` ${pluriel(nombres.avertissement, 'avertissement')} à vérifier.`
			: '';

	if (!compteRendu.importable) {
		return (
			<Message negative role="alert">
				<Message.Header>
					<Icon
						iconSet="Lucide"
						name="OctagonX"
						color="currentColor"
						size={18}
						aria-hidden="true"
					/>{' '}
					{etape === 'analyse'
						? 'Fichier refusé'
						: "Import refusé : rien n'a été écrit"}
				</Message.Header>
				<p>
					{pluriel(nombres.bloquant, 'cause bloquante', 'causes bloquantes')} à
					corriger dans le fichier avant de le déposer de nouveau.
				</p>
			</Message>
		);
	}

	return (
		<Message positive role="status">
			<Message.Header>
				<Icon
					iconSet="Lucide"
					name="CircleCheck"
					color="currentColor"
					size={18}
					aria-hidden="true"
				/>{' '}
				{etape === 'analyse'
					? 'Le fichier peut être importé'
					: 'Import terminé'}
			</Message.Header>
			<p>
				{etape === 'analyse'
					? "Rien n'a encore été écrit. Vérifiez les avertissements, puis lancez l'import."
					: `Pièces enregistrées au brouillard${
							Array.isArray(bordereaux) && bordereaux.length > 0
								? `, ${bordereaux.length > 1 ? 'bordereaux' : 'bordereau'} n° ${bordereaux.join(', ')}`
								: ''
						}.`}
				{avertissements}
			</p>
		</Message>
	);
};

/**
 * Compte rendu d'analyse ou d'import d'un flux PES : verdict, chiffres du fichier, puis les anomalies regroupées par
 * cause, un segment par niveau (bloquants dépliés d'office), filtrables par niveau et par texte (nom de tiers,
 * numéro de pièce, nature…), exportables en CSV pour corriger le fichier.
 */
const CompteRenduImport = ({
	etape,
	compteRendu,
	nomFichier,
}: CompteRenduImportProps) => {
	const [niveaux, setNiveaux] = React.useState<
		Record<NiveauAnomalieImportPes, boolean>
	>({
		bloquant: true,
		avertissement: true,
		information: true,
	});
	const [recherche, setRecherche] = React.useState('');
	const anomalies = React.useMemo(
		() =>
			compteRendu.anomalies.map((anomalie, rang) => ({
				anomalie,
				cle: cleAnomalie(anomalie, rang),
				texte: texteAnomalie(anomalie),
			})),
		[compteRendu],
	);
	const nombres = React.useMemo(
		() => compterParNiveau(compteRendu.anomalies),
		[compteRendu],
	);
	// colonne Fichier des pièces : seulement si l'import compte plusieurs XML (zip)
	const avecFichier = Number(compteRendu.resume.flux ?? 1) > 1;
	const [ouverts, setOuverts] = React.useState<Set<string>>(() => {
		const bloquants = anomalies.filter(
			({ anomalie }) => anomalie.niveau === 'bloquant',
		);
		return new Set(
			bloquants.length <= BLOQUANTS_DEPLIES
				? bloquants.map(({ cle }) => cle)
				: [],
		);
	});

	const terme = normaliser(recherche.trim());
	const visibles = anomalies.filter(
		({ anomalie, texte }) =>
			niveaux[anomalie.niveau] && (terme === '' || texte.includes(terme)),
	);
	const basculer = (cle: string) =>
		setOuverts((actuels) => {
			const suivants = new Set(actuels);
			if (!suivants.delete(cle)) {
				suivants.add(cle);
			}
			return suivants;
		});
	const toutOuvert =
		visibles.length > 0 && visibles.every(({ cle }) => ouverts.has(cle));
	const chiffres = LIBELLES_RESUME.filter(
		([cle]) => typeof compteRendu.resume[cle] === 'number',
	);

	return (
		<section aria-labelledby={`compte-rendu-${etape}`}>
			<Header as="h3" dividing id={`compte-rendu-${etape}`}>
				{etape === 'analyse' ? "Résultat de l'analyse" : "Résultat de l'import"}{' '}
				<Label basic size="small">
					<Icon iconSet="Lucide" name="FileText" size={12} aria-hidden="true" />{' '}
					{nomFichier}
				</Label>
			</Header>

			<Verdict etape={etape} compteRendu={compteRendu} nombres={nombres} />

			{chiffres.length > 0 && (
				// grille : une colonne par chiffre, moitié moins par ligne sur tablette ; dans un segment pour garder
				// l'espace avec les filtres (marges négatives de la grille)
				<Segment basic>
					<Grid
						columns={chiffres.length as SemanticWIDTHS}
						doubling
						textAlign="center"
					>
						{chiffres.map(([cle, libelle]) => (
							<Grid.Column key={cle}>
								<Statistic size="tiny">
									<Statistic.Value>
										{Number(compteRendu.resume[cle]).toLocaleString('fr-FR')}
									</Statistic.Value>
									<Statistic.Label>{libelle}</Statistic.Label>
								</Statistic>
							</Grid.Column>
						))}
					</Grid>
				</Segment>
			)}

			{compteRendu.anomalies.length === 0 ? (
				<Message info>Aucune anomalie : le fichier est conforme.</Message>
			) : (
				<>
					<Form
						as="search"
						onSubmit={(event: React.FormEvent) => event.preventDefault()}
					>
						<Form.Group inline>
							{ORDRE_NIVEAUX.map((niveau) => {
								const total = nombres[niveau];
								return (
									<Form.Field key={niveau}>
										<Checkbox
											// avec un id, Semantic relie le libellé à la case (nom lu par les lecteurs d'écran)
											id={`filtre-${etape}-${niveau}`}
											toggle
											disabled={total === 0}
											checked={niveaux[niveau] && total > 0}
											label={`${NIVEAUX[niveau].titre} (${total})`}
											onChange={(_event, { checked }) =>
												setNiveaux((actuels) => ({
													...actuels,
													[niveau]: Boolean(checked),
												}))
											}
										/>
									</Form.Field>
								);
							})}
						</Form.Group>
						<Form.Group widths="equal">
							<Form.Field>
								<Input
									id={`recherche-${etape}`}
									icon="search"
									placeholder="Rechercher un tiers, une pièce, une nature…"
									aria-label="Rechercher dans le compte rendu"
									value={recherche}
									debounced={false}
									onChange={(
										_event: React.ChangeEvent<HTMLInputElement>,
										{ value }: { value: string },
									) => setRecherche(value)}
								/>
							</Form.Field>
							<Form.Field>
								<Button.Group basic size="small" fluid>
									<Button
										type="button"
										disabled={visibles.length === 0}
										onClick={() =>
											setOuverts(
												toutOuvert
													? new Set()
													: new Set(visibles.map(({ cle }) => cle)),
											)
										}
									>
										{toutOuvert ? 'Tout replier' : 'Tout déplier'}
									</Button>
									<Button
										type="button"
										onClick={() => telechargerCsv(compteRendu, nomFichier)}
									>
										<Icon
											iconSet="Lucide"
											name="Download"
											size={14}
											aria-hidden="true"
										/>{' '}
										Exporter (CSV)
									</Button>
								</Button.Group>
							</Form.Field>
						</Form.Group>
					</Form>

					<p aria-live="polite">
						{pluriel(visibles.length, 'cause affichée', 'causes affichées')}
						{terme !== '' && ` pour « ${recherche.trim()} »`}
					</p>

					{visibles.length === 0 && (
						<Message>Aucune anomalie ne correspond aux filtres.</Message>
					)}

					{ORDRE_NIVEAUX.map((niveau) => {
						const duNiveau = visibles.filter(
							({ anomalie }) => anomalie.niveau === niveau,
						);
						if (duNiveau.length === 0) {
							return null;
						}
						return (
							<Segment
								key={niveau}
								color={NIVEAUX[niveau].couleur}
								as="section"
								aria-labelledby={`niveau-${etape}-${niveau}`}
							>
								<Header
									as="h4"
									color={NIVEAUX[niveau].couleur}
									id={`niveau-${etape}-${niveau}`}
								>
									<Icon
										iconSet="Lucide"
										name={NIVEAUX[niveau].icone}
										color="currentColor"
										size={20}
										aria-hidden="true"
									/>{' '}
									{NIVEAUX[niveau].titre}{' '}
									<Label circular size="small" color={NIVEAUX[niveau].couleur}>
										{duNiveau.length}
									</Label>
									<Header.Subheader>
										{NIVEAUX[niveau].description}
									</Header.Subheader>
								</Header>
								<Accordion fluid>
									{duNiveau.map(({ anomalie, cle }) => (
										<GroupeAnomalie
											key={cle}
											anomalie={anomalie}
											identifiant={`${etape}-${cle.replace(/[^a-z0-9]/gi, '-')}`}
											ouvert={ouverts.has(cle)}
											onBasculer={() => basculer(cle)}
											avecFichier={avecFichier}
										/>
									))}
								</Accordion>
							</Segment>
						);
					})}
				</>
			)}
		</section>
	);
};

export default CompteRenduImport;
